/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect } from 'react';
import TextField from '@mui/material/TextField';
import useGooglePlaces from '../../utils/useGooglePlaces';
import { FormControl } from '@mui/material';

interface AutocompleteFieldProps {
  isRequired?: boolean;
  label: string;
  name: string;
  value: string;
  onChange: (addressValues: any) => void;
  errors?: string;
  setErrors?: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
  errorObjectName?: string;
}

const AutocompleteField: React.FC<AutocompleteFieldProps> = ({
  isRequired,
  label,
  name,
  value,
  onChange,
  errors,
  setErrors,
  errorObjectName,
}) => {
  const { ref, fullAddress, setFullAddress } = useGooglePlaces((place) => {
    const addressComponents = place.address_components;
    const formattedAddress = place.formatted_address;
    const updatedValues: any = {};
    if (!addressComponents) return;
    addressComponents.forEach(
      (component: {
        types: string[];
        long_name: string;
        short_name: string;
      }) => {
        let componentType = component.types[0];
        if (componentType === 'political') {
          componentType = component.types[1];
        }

        switch (componentType) {
          case 'street_number': {
            updatedValues.address =
              component.long_name !== 'undefined'
                ? component.long_name
                : component.short_name;
            break;
          }
          case 'route': {
            updatedValues.address = `${updatedValues.address ?? ''} ${
              component.short_name
            }`.trim();
            break;
          }
          case 'locality':
            {
              updatedValues.city = component.long_name;
            }
            break;
          case 'administrative_area_level_1': {
            updatedValues.state = component.short_name;
            break;
          }
          case 'postal_code':
            updatedValues.zip = component.long_name;
        }
      }
    );
    updatedValues.fullAddress = formattedAddress;
    onChange({ ...updatedValues });
    setFullAddress(formattedAddress);
    const hasEssentialComponents = [
      'route',
      'locality',
      'administrative_area_level_1',
    ].every((type) =>
      addressComponents.some((comp: any) => comp.types.includes(type))
    );

    if (!hasEssentialComponents) {
      setErrors?.((prev) => ({
        ...prev,
        [errorObjectName!]:
          'Incomplete address. Please select a valid address.',
      }));
    } else {
      setErrors?.((prev) => ({ ...prev, [errorObjectName!]: '' }));
    }
  }, value);

  useEffect(() => {
    if (value) setFullAddress(value);
  }, [setFullAddress, value]);

  return (
    <FormControl fullWidth sx={{ marginTop: '1.6rem' }}>
      <TextField
        placeholder={value || ''}
        fullWidth
        label={label}
        required={!!isRequired}
        name={name}
        error={!!errors}
        helperText={errors || ''}
        inputRef={ref}
        value={fullAddress || ''}
        onChange={(e) => {
          // if (!isRequired) {
          // Validate address format (basic check)
          const isAddressValid = /\d+\s+\w+/.test(e.target.value);
          if (!isAddressValid && e.target.value) {
            setErrors?.((prev) => ({
              ...prev,
              [errorObjectName!]: 'Please pick an address from the suggestions',
            }));
          } else if (isAddressValid || e.target.value === '') {
            setErrors?.((prev) => ({ ...prev, [errorObjectName!]: '' }));
          }
          // }
          if (e.target.value === '') {
            onChange({
              address: '',
              city: '',
              state: '',
              fullAddress: '',
            });
            if (isRequired) {
              setErrors!({
                [`${errorObjectName}`]: 'This field is required',
              });
            }
          }

          setFullAddress(e.target.value);
        }}
      />
    </FormControl>
  );
};

export default AutocompleteField;
